/**
 * Кто вошёл и что ему видно.
 *
 * Роль здесь решает только видимость разделов. Настоящая проверка прав — на сервере
 * в каждом роуте (§5.1): спрятанная кнопка это не защита, а удобство.
 */

import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { Role, SessionUser } from '@cc/shared';

import { ApiError, fetchSession } from '../shared/api/client.js';

export const SESSION_QUERY_KEY = ['session'] as const;

interface SessionState {
  user: SessionUser | null;
  isLoading: boolean;
}

const SessionContext = createContext<SessionState>({ user: null, isLoading: true });

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    // 401 — это ответ «никто не вошёл», повторять его бессмысленно
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isUnauthorized) && failureCount < 2,
    staleTime: 5 * 60_000,
  });

  const value: SessionState = {
    user: query.data ?? null,
    isLoading: query.isPending,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

/** Пользователь, который точно есть — внутри AuthGuard */
export function useCurrentUser(): SessionUser {
  const { user } = useSession();
  if (!user) throw new Error('useCurrentUser вызван вне AuthGuard');
  return user;
}

export interface AuthGuardProps {
  /** Пусто — достаточно быть залогиненным */
  roles?: Role[];
  children: ReactNode;
}

export function AuthGuard({ roles, children }: AuthGuardProps): React.JSX.Element {
  const { user, isLoading } = useSession();

  if (isLoading) {
    return <p className="p-8 text-slate-500">Загружаю…</p>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-medium text-slate-900">Раздел недоступен</h1>
        <p className="mt-2 text-slate-600">
          У роли «{user.role}» нет доступа к этому разделу. Обратитесь к администратору.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
